import { useEffect, useState } from "react";
import api, { qs } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { brandColor, basisLabel, fmt, downloadCSV } from "@/lib/utils-msip";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";

const Cell = ({ share, count, highlight, inverse, kerala }) => (
  <td className={`px-2 py-1 text-right whitespace-nowrap ${highlight ? (kerala ? "bg-white/15" : "bg-amber-100/60") : ""}`}>
    <div className={`num font-semibold text-[13px] ${inverse ? "" : ""}`}>{share != null ? `${share.toFixed(2)}%` : "—"}</div>
    <div className={`num text-[10px] ${inverse ? "opacity-70" : "text-muted-foreground"}`}>{fmt(count)}</div>
  </td>
);

const ROW_STYLE = {
  district: "",
  kerala: "bg-foreground text-background font-bold",
  state: "",
  states_total: "bg-secondary font-bold",
  combined: "bg-secondary font-bold",
};

export default function SummaryMatrix() {
  const { filters } = useApp();
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get(`/summary?${qs({ basis: filters.basis, period: filters.period })}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, [filters.basis, filters.period]);

  if (!data) return null;
  const brands = data.brands;

  const exportCsv = () => {
    const all = [...data.rows, ...(data.states || []), ...(data.states_total ? [data.states_total] : []), ...(data.combined ? [data.combined] : [])];
    downloadCSV(
      `market_summary_${filters.period}.csv`,
      ["Market", "TOTAL", ...brands, ...brands.map((b) => `${b} %`)],
      all.map((r) => [r.name, r.total, ...brands.map((b) => r.counts[b]), ...brands.map((b) => r.shares[b])]),
      filters.basis
    );
  };

  const Row = ({ r, inverse }) => (
    <tr className={`border-b last:border-0 ${ROW_STYLE[r.type] || ""} ${r.type === "district" || r.type === "state" ? "hover:bg-muted/50" : ""}`}>
      <td className={`px-3 py-1.5 whitespace-nowrap text-[13px] ${r.type === "district" ? "pl-5 font-medium" : "font-bold"}`}>
        {r.name}{r.type === "district" ? " DISTRICT" : ""}
      </td>
      <td className="px-2 py-1 text-right num font-bold text-[13px]">{fmt(r.total)}</td>
      {brands.map((b) => (
        <Cell key={b} share={r.shares[b]} count={r.counts[b]} highlight={b === filters.brand} inverse={inverse} kerala={r.type === "kerala"} />
      ))}
    </tr>
  );

  return (
    <div className="border bg-card rounded-md" data-testid="summary-matrix">
      <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b">
        <h3 className="font-heading text-sm font-semibold">Market Summary — District × Brand</h3>
        <span className="text-xs text-muted-foreground">{data.period?.label} · {basisLabel(filters.basis)} · share % over units</span>
        <Button variant="outline" size="sm" className="ml-auto print-hidden" onClick={exportCsv} data-testid="summary-export-csv-btn">
          <Download size={14} className="mr-1" /> CSV
        </Button>
      </div>
      <div className="overflow-auto max-h-[70vh]">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-card z-10 border-b">
            <tr>
              <th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Market</th>
              <th className="px-2 py-2 text-right text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Total</th>
              {brands.map((b) => (
                <th key={b} className={`px-2 py-2 text-right text-[10px] font-bold whitespace-nowrap ${b === filters.brand ? "bg-amber-100/60" : ""}`} style={{ color: brandColor(b) }}>{b}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => <Row key={r.name + r.type} r={r} inverse={r.type === "kerala"} />)}
            {data.states?.length > 0 && (
              <>
                <tr className="border-b bg-muted/60">
                  <td colSpan={brands.length + 2} className="px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Neighbouring states</td>
                </tr>
                {data.states.map((r) => <Row key={r.name} r={r} />)}
                {data.states_total && <Row r={data.states_total} />}
                {data.combined && <Row r={data.combined} />}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
